import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CaretDownIcon from './vectors/CaretDownIcon';
import CrossIcon from './vectors/CrossIcon';
import Typography from '@mui/material/Typography';

interface CountrySelectInputProps {
  name: string;
  label?: string;
  value: CountryType | null;
  onChange: (value: CountryType | null) => void;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  placeholder?: string;
  required?: boolean;
}

export default function CountrySelect(props: CountrySelectInputProps) {
  const {
    fullWidth = true,
    value,
    placeholder,
    onChange,
    onBlur,
    name,
    error,
    helperText,
    label,
    required,
  } = props;

  return (
    <Autocomplete
      fullWidth={fullWidth}
      id={name}
      options={countries}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      onBlur={onBlur}
      autoHighlight
      getOptionLabel={(option) => option.label}
      popupIcon={<CaretDownIcon />}
      clearIcon={<CrossIcon />}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box key={key} component="li" {...optionProps} style={{ padding: '6px 5px' }}>
            <Box
              sx={{
                mr: 2,
                px: '2px',
                py: '3px',
                flexShrink: 0,
                overflow: 'hidden',
                alignItems: 'center',
                display: 'inline-flex',
                justifyContent: 'center',
                bgcolor: 'background.neutral',
              }}
            >
              <Box
                component="img"
                loading="lazy"
                alt={option.code}
                src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${option.code?.toUpperCase()}.svg`}
                sx={{
                  width: 20,
                  height: 14,
                  maxWidth: 'unset',
                  objectFit: 'cover',
                  borderRadius: '1px',
                }}
              />
            </Box>
            <Typography variant="body2">{option.label}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          required={required}
          label={label}
          error={error}
          placeholder={placeholder}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password', // disable autocomplete and autofill
          }}
        />
      )}
    />
  );
}

interface CountryType {
  code: string;
  label: string;
  suggested?: boolean;
}

// From https://bitbucket.org/atlassian/atlaskit-mk-2/raw/4ad0e56649c3e6c973e226b7efaeb28cb240ccb0/packages/core/select/src/data/countries.js
const countries: readonly CountryType[] = [
  { code: 'AU', label: 'Australia' },
  { code: 'AT', label: 'Austria' },
  { code: 'BE', label: 'Belgium' },
  { code: 'BR', label: 'Brazil' },
  { code: 'BG', label: 'Bulgaria' },
  { code: 'CA', label: 'Canada' },
  { code: 'HR', label: 'Croatia' },
  { code: 'CY', label: 'Cyprus' },
  { code: 'CZ', label: 'Czech Republic' },
  { code: 'DK', label: 'Denmark' },
  { code: 'EE', label: 'Estonia' },
  { code: 'FI', label: 'Finland' },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' },
  { code: 'GH', label: 'Ghana' },
  { code: 'GI', label: 'Gibraltar' },
  { code: 'GR', label: 'Greece' },
  { code: 'HK', label: 'Hong Kong' },
  { code: 'HU', label: 'Hungary' },
  { code: 'IE', label: 'Ireland' },
  { code: 'IT', label: 'Italy' },
  { code: 'JP', label: 'Japan' },
  { code: 'KE', label: 'Kenya' },
  { code: 'LV', label: 'Latvia' },
  { code: 'LI', label: 'Liechtenstein' },
  { code: 'LT', label: 'Lithuania' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'MT', label: 'Malta' },
  { code: 'MX', label: 'Mexico' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'NG', label: 'Nigeria' },
  { code: 'NO', label: 'Norway' },
  { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'RO', label: 'Romania' },
  { code: 'SG', label: 'Singapore' },
  { code: 'SK', label: 'Slovakia' },
  { code: 'SI', label: 'Slovenia' },
  { code: 'ZA', label: 'South Africa' },
  { code: 'ES', label: 'Spain' },
  { code: 'SE', label: 'Sweden' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'TH', label: 'Thailand' },
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
];
